/**
 * Everything the Credits page does that is not rendering.
 *
 * The six-step filter pipeline stays on the server, in `credits_logic`, whose
 * step ORDER is load-bearing and documented there. This module builds the
 * query, sends the four writes, and maps the server's semantic verdicts onto
 * the badges a person reads.
 *
 * Those verdicts are the reason the mapping lives here rather than in JSX: the
 * server says "expiring", the browser decides that looks like a red dot. If
 * the browser decided *whether* a credit is expiring, that rule would exist
 * twice.
 */

import { authedFetch } from "./api";
import { orThrow } from "./http";

/** One cadence's "3/8 used" rollup, from `summarize_usage_by_cadence`. */
export interface CadenceUsage {
  /** The raw cadence ("monthly", "quarterly", ...). The stable identity of the
   *  row; `label` is display text and changes when the wording does. */
  cadence: string;
  label: string;
  pct: number;
  used_count: number;
  credit_count: number;
  total_used: number;
  total_available: number;
}

export interface CreditStatusDisplay {
  used: number;
  remaining: number;
  amount: number;
  pct: number;
  days_left: number;
  period_label: string;
  period_urgent: boolean;
  urgency: string | null;
  category: string;
  end_warning: string | null;
}

export interface ActiveCredit {
  id: number;
  /** Stable identity for card navigation; optional during API rollout. */
  card_id?: number;
  name: string;
  card_name: string;
  cadence: string;
  period_start: string;
  period_end: string;
  display: CreditStatusDisplay;
}

export interface MarkUsedDefaults {
  max_amount: number;
  amount: number;
  date: string;
  disabled: boolean;
}

export interface FilterOptions {
  card_names: string[];
  cadence_labels: string[];
  cadence_reverse: Record<string, string>;
  cadence_display: Record<string, string>;
  category_options: string[];
}

/**
 * One row of a credit's usage history for the current period.
 *
 * `id` is merged in by the router; `usage_row` itself has no id, because the
 * live view keys its buttons off the raw row it already holds.
 */
export interface UsageRow {
  id: number;
  amount_used: number;
  date_used: string;
  status: string;
  notes: string;
  /** The server's verdict: pending rows offer Confirm, everything else Undo. */
  action: "confirm" | "undo";
}

export interface PendingUsage {
  id: number;
  credit_name: string;
  card_name: string;
  amount_used: number;
  date_used: string;
  /** "manual" for a tap or a typed form; AUTO_MARK_SOURCE for the sweep. */
  source: string;
  notes: string | null;
}

/** `credit_usage.source` of a redemption the post-day sweep wrote. */
export const AUTO_MARK_SOURCE = "auto_post_day";

export interface CreditsDto {
  today: string;
  options: FilterOptions;
  active: ActiveCredit[];
  inactive: Record<string, unknown>[];
  discontinued: Record<string, unknown>[];
  /** Hidden until `snoozed_until`, the day each comes back. */
  snoozed: SnoozedCredit[];
  is_empty: boolean;
  mark_used_defaults: Record<string, MarkUsedDefaults>;
  /** The group headers' "3/8 used", from the dashboard logic's rollup. */
  usage_by_cadence: CadenceUsage[];
}

export interface SnoozedCredit {
  id: number;
  card_id?: number;
  name: string;
  card_name: string;
  cadence: string;
  snoozed_until: string;
}

export interface CreditFilters {
  card?: string;
  cadence?: string;
  category?: string;
  hideUsed?: boolean;
}

/**
 * Build the query string.
 *
 * Only non-default filters are sent. The server's defaults and these must
 * agree, and omitting a filter is how you say "no filter" -- sending the
 * sentinel string would work too, but it puts the sentinel's exact spelling
 * ("All Cards", capital C, which differs from the Offers page's "All cards")
 * into the URL where a typo becomes an empty page rather than an error.
 */
export function creditsQuery(f: CreditFilters): string {
  const q = new URLSearchParams();
  if (f.card) q.set("card", f.card);
  if (f.cadence) q.set("cadence", f.cadence);
  if (f.category) q.set("category", f.category);
  // Explicit both ways: the server defaults to true, so "show everything"
  // has to be sent or it is silently ignored.
  if (f.hideUsed !== undefined) q.set("hide_used", String(f.hideUsed));
  const s = q.toString();
  return s ? `?${s}` : "";
}

export async function fetchCredits(f: CreditFilters = {}): Promise<CreditsDto> {
  const res = await authedFetch(`/api/credits${creditsQuery(f)}`);
  return (await orThrow(res, "Could not load credits")) as CreditsDto;
}

export async function fetchPending(): Promise<PendingUsage[]> {
  const res = await authedFetch("/api/credits/pending");
  const body = (await orThrow(res, "Could not load pending redemptions")) as {
    pending: PendingUsage[];
  };
  return body.pending;
}

/**
 * The post-day sweep: the server writes a pending redemption for every credit
 * whose recurring post day has arrived this period with nothing recorded, and
 * says how many. Idempotent per credit and period, so calling it on every
 * page load is what makes "once the date arrives it shows up as pending"
 * true without a scheduler.
 */
export async function autoMarkPosted(): Promise<{ marked: number }> {
  const res = await authedFetch("/api/credits/auto-mark", { method: "POST" });
  return (await orThrow(res, "Could not check for posted credits")) as {
    marked: number;
  };
}

export async function recordUsage(
  creditId: number,
  input: {
    amount_used: number;
    date_used: string;
    period_start: string;
    period_end: string;
    status?: string;
    notes?: string | null;
  },
): Promise<number> {
  const res = await authedFetch(`/api/credits/${creditId}/usage`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  const body = (await orThrow(res, "Could not record the redemption")) as {
    id: number;
  };
  return body.id;
}

/** Confirm: pending -> posted. */
/**
 * One credit's redemptions for one period.
 *
 * The endpoint existed and nothing called it, so the port had no per-credit
 * usage history at all -- and with it went the only place a POSTED redemption
 * can be undone. The Pending Confirmation list at the top of the page shows
 * pending rows only, so a redemption entered as "posted", or confirmed by
 * mistake, was unreachable.
 *
 * The period boundaries come from the credit row the server sent, never from
 * a date computed here: `build_credit_list` decides what period a credit is
 * in, and a second opinion in the browser would ask for the wrong window on
 * exactly the credits whose reset schedule is not the calendar.
 */
export async function fetchUsage(
  creditId: number,
  periodStart: string,
  periodEnd: string,
): Promise<UsageRow[]> {
  const q = new URLSearchParams({
    period_start: periodStart,
    period_end: periodEnd,
  });
  const res = await authedFetch(`/api/credits/${creditId}/usage?${q.toString()}`);
  const body = (await orThrow(res, "Could not load the usage history")) as {
    usage: UsageRow[];
  };
  return body.usage;
}

export async function confirmUsage(usageId: number): Promise<void> {
  const res = await authedFetch(`/api/usage/${usageId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status: "posted" }),
  });
  await orThrow(res, "Could not confirm the redemption");
}

/** Remove a pending redemption, or undo a posted one. Destroys the row. */
export async function deleteUsage(usageId: number): Promise<void> {
  const res = await authedFetch(`/api/usage/${usageId}`, { method: "DELETE" });
  await orThrow(res, "Could not remove the redemption");
}

export async function setActivation(
  creditId: number,
  activated: boolean,
): Promise<void> {
  const res = await authedFetch(`/api/credits/${creditId}/activation`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ activated }),
  });
  await orThrow(res, "Could not change activation");
}

/** Snooze a credit until an ISO day, or wake it with null. */
export async function setSnooze(
  creditId: number,
  until: string | null,
): Promise<void> {
  const res = await authedFetch(`/api/credits/${creditId}/snooze`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ until }),
  });
  await orThrow(res, "Could not change the snooze");
}

/**
 * The dot beside a credit, for the server's urgency verdict.
 *
 * The server decides WHICH verdict; this decides what it looks like. An
 * unknown verdict gets no dot rather than a wrong one: a missing dot is a
 * missing feature, a mis-colored dot is a lie about a deadline.
 */
const URGENCY_DOT: Record<string, string> = {
  expiring: "bg-red-400",
  soon: "bg-amber-400",
};

export function urgencyDot(urgency: string | null): string | null {
  if (!urgency) return null;
  return URGENCY_DOT[urgency] ?? null;
}

const STATUS_ICON: Record<string, string> = {
  pending: "⏳",
  posted: "✅",
};

/** Matches the view's `_STATUS_ICON.get(status, "❌")`. */
export function statusIcon(status: string): string {
  return STATUS_ICON[status] ?? "❌";
}

/**
 * The display name for a cadence, straight from the server's `CADENCE_DISPLAY`.
 *
 * An unrecognized cadence renders as its raw value: a heading reading
 * `every_4_years` is ugly, and a group of credits under no heading is worse.
 */
export function cadenceLabel(
  cadence: string,
  display: Record<string, string>,
): string {
  return display[cadence] ?? cadence;
}

export interface CadenceGroup {
  cadence: string;
  open: ActiveCredit[];
  used: ActiveCredit[];
}

/**
 * The page's sections, from the server's verdicts and in the server's order.
 *
 * Expiring holds exactly the "soon" and "expiring" verdicts (7 days or fewer,
 * 3 or fewer, both decided in `credits_logic.urgency`), so it only exists in a
 * credit's last week. The rest group by cadence in arrival order, and a "used"
 * credit folds under its own cadence instead of leaving the page. (2026-09-10)
 */
export function creditSections(active: ActiveCredit[]): {
  expiring: ActiveCredit[];
  groups: CadenceGroup[];
} {
  const expiring: ActiveCredit[] = [];
  const groups: CadenceGroup[] = [];
  for (const c of active) {
    const u = c.display.urgency;
    if (u === "expiring" || u === "soon") {
      expiring.push(c);
      continue;
    }
    let g = groups.find((x) => x.cadence === c.cadence);
    if (!g) {
      g = { cadence: c.cadence, open: [], used: [] };
      groups.push(g);
    }
    (u === "used" ? g.used : g.open).push(c);
  }
  return { expiring, groups };
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * "Dec 31" from an ISO date, read as a calendar day. Never through `Date`,
 * which parses it as UTC midnight and shows the day before west of Greenwich.
 * The year is added only when it differs from `today`'s.
 */
export function monthDay(iso: string, today?: string): string {
  const [y, m, d] = iso.split("-");
  const base = `${MONTHS[Number(m) - 1]} ${Number(d)}`;
  return today && today.slice(0, 4) !== y ? `${base}, ${y}` : base;
}

/** An ISO day moved by whole months (clamped to the month's last day). */
export function addMonthsIso(iso: string, months: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const last = new Date(Date.UTC(y, m - 1 + months + 1, 0)).getUTCDate();
  return new Date(Date.UTC(y, m - 1 + months, Math.min(d, last)))
    .toISOString()
    .slice(0, 10);
}

/** The day after an ISO day. */
export function nextDayIso(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
}

/**
 * The Snooze picker's quick dates: the first day of the credit's next period,
 * then a month and three months out. Each is a day the credit comes BACK.
 */
export function snoozeChoices(
  periodEnd: string,
  today: string,
): { label: string; until: string }[] {
  const next = nextDayIso(periodEnd);
  return [
    { label: `Next period · ${monthDay(next, today)}`, until: next },
    { label: "1 month", until: addMonthsIso(today, 1) },
    { label: "3 months", until: addMonthsIso(today, 3) },
  ];
}

/**
 * When a credit runs out, as its row shows it: days while within a month, the
 * period's last day after that, since "112d" reads as noise. Presentation
 * only; which credits are urgent is the server's verdict.
 */
export function deadlineText(c: ActiveCredit, today: string): string {
  return c.display.days_left <= 31
    ? `${c.display.days_left}d`
    : monthDay(c.period_end, today);
}

/**
 * The page's headline: what is left on the credits that run out first, and
 * when. Period ends and amounts are the server's; picking the earliest is the
 * only thing decided here. Null when nothing is left anywhere.
 */
export function nextDeadline(
  active: ActiveCredit[],
): { date: string; remaining: number } | null {
  const open = active.filter((c) => c.display.remaining > 0);
  if (open.length === 0) return null;
  const date = open.reduce(
    (min, c) => (c.period_end < min ? c.period_end : min),
    open[0].period_end,
  );
  const cents = open
    .filter((c) => c.period_end === date)
    .reduce((sum, c) => sum + Math.round(c.display.remaining * 100), 0);
  return { date, remaining: cents / 100 };
}
